// Brand Console DATA -- Ecommerce.
//
// Consumes ONLY the real Shopify connector -> intelligence_bridge ->
// intelligence_measurements pipeline (connectors/shopify.py,
// connectors/intelligence_bridge.py's own COMMERCE_METRIC_SPECS) via the
// existing GET /intelligence/{workspace_id}/measurements route
// (source_type=CONNECTOR) and GET /connectors/{workspace_id} for
// connection state. Deliberately does NOT read the legacy
// /marketplace/* routes (MarketplacePage.js's own data source) --
// confirmed fabricated, never wired into this page.
//
// Real schema constraint, verified directly in
// connectors/intelligence_bridge.py's ingest_commerce_record (not
// assumed): "order" records are attributed to the single "brand" entity
// (revenue/refunds/discounts/orders/units) -- there is no per-order
// entity. "product" records get their own entity (price/inventory only
// -- no per-product revenue is ever written). So the Products section
// below honestly shows price/inventory per product, not a per-product
// revenue breakdown the data model does not actually store -- inventing
// one would be exactly the fabrication this task forbids.

import { useEffect, useState, useCallback, useMemo } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { DashboardLayout } from '@/components/DashboardLayout';
import { EmptyState, ErrorState, LoadingState } from '@/components/brand/StateViews';
import { StatusBadge, ConfidenceIndicator } from '@/components/brand/Badges';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { measurementsAPI, connectorsAPI, entitiesAPI } from '@/lib/intelligenceApi';
import { shopifyAPI } from '@/lib/api';
import { WorkspaceSelect } from '@/components/brand/BrandContextBar';
import { useWorkspaceSelection } from '@/hooks/useWorkspaceSelection';
import { useNavigate } from 'react-router-dom';

function providerOf(measurement) {
  return (measurement.source_reference || '').split(':')[0];
}
// A metric the connector never reported is unknown, not zero: the summary
// only totals metric_keys that actually have (non-invalidated) records, and
// says which state applies otherwise. A genuinely reported 0 stays 0.
export function metricTotal(records, invalidated, key) {
  const rows = records.filter((r) => r.metric_key === key && typeof r.value === 'number');
  if (rows.length) return { state: 'reported', value: rows.reduce((acc, r) => acc + r.value, 0) };
  if (invalidated.some((r) => r.metric_key === key)) return { state: 'invalidated', value: null };
  return { state: 'not_reported', value: null };
}

// Money measurements are stored with unit "currency" (a type, not a code):
// the Shopify connector does not persist the store's currency code today.
// A code is used only when a record itself carries one; otherwise amounts
// are shown without a currency and the page says so. Never guessed.
const ISO_CURRENCY = /^[A-Z]{3}$/;
export function currencyOf(records, keys) {
  const codes = new Set(
    records.filter((r) => keys.includes(r.metric_key))
      .map((r) => r.metadata?.currency_code || r.metadata?.currency || (ISO_CURRENCY.test(r.unit || '') ? r.unit : null))
      .filter((c) => typeof c === 'string' && ISO_CURRENCY.test(c)),
  );
  if (codes.size === 0) return { code: null, mixed: false };
  if (codes.size > 1) return { code: null, mixed: true };
  return { code: [...codes][0], mixed: false };
}

function formatNumber(n, digits = 0) {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  return n.toLocaleString(undefined, { maximumFractionDigits: digits });
}
function formatMoney(n, code) {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  if (!code) return formatNumber(n, 2);
  try { return n.toLocaleString(undefined, { style: 'currency', currency: code }); } catch { return `${formatNumber(n, 2)} ${code}`; }
}
const METRIC_STATE_NOTE = { not_reported: 'Not reported', invalidated: 'Invalidated', no_orders: 'No orders to average', mixed_currency: 'Mixed currencies' };

// Connector lifecycle (connectors/registry.py): CONNECTED until the first
// health check or sync, then HEALTHY. Only a disconnected or revoked
// connector means "not connected"; the others still hold synced data.
const INACTIVE_CONNECTOR = ['DISCONNECTED', 'CREDENTIAL_REVOKED'];
const CONNECTION_NOTE = {
  ACTION_REQUIRED: 'Shopify needs you to reconnect. New data will not arrive until you do.',
  EXPIRED: 'The Shopify connection has expired. New data will not arrive until you reconnect.',
  ERROR: 'The Shopify connection has an error. New data may not arrive.',
};
// SyncStatus (connectors/base.py). SUCCESS needs no note.
const SYNC_NOTE = {
  NEVER_SYNCED: 'Shopify has not synced yet.',
  SYNCING: 'A sync is running. Figures may change when it finishes.',
  PARTIAL: 'The last sync was partial, so these figures may be incomplete.',
  FAILED: 'The last sync failed. Figures are from earlier syncs and may be out of date.',
  STALE: 'This data is stale. Figures may be out of date.',
};
export function connectionNote(connector) {
  if (!connector) return null;
  return CONNECTION_NOTE[connector.status] || SYNC_NOTE[connector.last_sync_status] || null;
}

export default function EcommercePage() {
  const navigate = useNavigate();
  // P1.9: client resolution is now the shared useWorkspaceSelection() hook
  // (see SignalsPage.js's own comment for the full rationale) instead of
  // an unswitchable first-client fetch.
  const { clients, workspaceId, setWorkspaceId, loadingClients } = useWorkspaceSelection();
  const clientId = workspaceId || null;
  const noClient = !loadingClients && clients.length === 0;
  const [connectors, setConnectors] = useState(null);
  const [measurements, setMeasurements] = useState(null);
  const [productNames, setProductNames] = useState({});
  const [error, setError] = useState(null);
  const [sortKey, setSortKey] = useState('observed_at');
  const [sortDir, setSortDir] = useState('desc');
  const [selectedMeasurement, setSelectedMeasurement] = useState(null);
  const [commerceReport, setCommerceReport] = useState(null);
  const [refreshingReport, setRefreshingReport] = useState(false);
  const [refreshError, setRefreshError] = useState(null);

  const load = useCallback(() => {
    if (!clientId) return;
    setConnectors(null); setMeasurements(null); setError(null); setCommerceReport(null);
    Promise.all([
      connectorsAPI.list(clientId),
      measurementsAPI.listForWorkspace(clientId, { source_type: 'CONNECTOR' }),
      entitiesAPI.list(clientId, { entityType: 'product' }).catch(() => ({ data: { entities: [] } })),
      shopifyAPI.get(clientId).catch(() => ({ data: null })),
    ])
      .then(([connRes, measRes, entRes, reportRes]) => {
        setConnectors(connRes.data?.connectors || []);
        const shopifyRecords = (measRes.data?.measurements || []).filter((m) => providerOf(m) === 'shopify');
        setMeasurements(shopifyRecords);
        const names = {};
        (entRes.data?.entities || entRes.data || []).forEach((e) => { names[e.id] = e.canonical_name || e.id; });
        setProductNames(names);
        setCommerceReport(reportRes?.data || null);
      })
      .catch((e) => setError(e));
  }, [clientId]);

  const refreshReport = useCallback(() => {
    if (!clientId || refreshingReport) return;
    setRefreshingReport(true);
    setRefreshError(null);
    shopifyAPI.refresh(clientId)
      .then(() => { load(); })
      .catch((error) => {
        const detail = error?.response?.data?.detail;
        const safe = typeof detail === 'string' && !/shpat_|shpca_|access_token|client_secret|bearer /i.test(detail)
          ? detail
          : 'Shopify could not be read right now. Try again shortly.';
        setRefreshError(safe);
      })
      .finally(() => setRefreshingReport(false));
  }, [clientId, load, refreshingReport]);

  useEffect(() => { load(); }, [load]);

  const connectedShopify = useMemo(
    () => (connectors || []).find((c) => c.provider === 'shopify' && !INACTIVE_CONNECTOR.includes(c.status)),
    [connectors],
  );
  const isConnected = !!connectedShopify;

  // Order-level records are attributed to entity_type "brand" (per the
  // real ingest path); product-level records to entity_type "product".
  // Final completeness audit finding: an INVALIDATED measurement is the
  // backend's own explicit "this record is no longer trustworthy" state
  // -- excluded here from every aggregated view (summary/trend/products);
  // the raw table below still shows every record, INVALIDATED included,
  // with its real status visible, for full transparency.
  const orderRecords = useMemo(() => (measurements || []).filter((m) => m.entity_type === 'brand' && m.status !== 'INVALIDATED'), [measurements]);
  const invalidatedOrderRecords = useMemo(() => (measurements || []).filter((m) => m.entity_type === 'brand' && m.status === 'INVALIDATED'), [measurements]);
  const productRecords = useMemo(() => (measurements || []).filter((m) => m.entity_type === 'product' && m.status !== 'INVALIDATED'), [measurements]);

  const summary = useMemo(() => {
    if (!measurements) return null;
    const currency = currencyOf(orderRecords, ['revenue', 'refunds']);
    const t = (key) => metricTotal(orderRecords, invalidatedOrderRecords, key);
    let revenue = t('revenue');
    let refunds = t('refunds');
    // Amounts in different currencies are never added together.
    if (currency.mixed) {
      revenue = { state: 'mixed_currency', value: null };
      refunds = { state: 'mixed_currency', value: null };
    }
    const orders = t('orders');
    const units = t('units');
    // AOV only from a reported revenue AND a reported, non-zero order count.
    let aov;
    if (revenue.state !== 'reported') aov = { state: revenue.state, value: null };
    else if (orders.state !== 'reported') aov = { state: orders.state, value: null };
    else if (orders.value === 0) aov = { state: 'no_orders', value: null };
    else aov = { state: 'reported', value: revenue.value / orders.value };
    const latestObserved = orderRecords.map((m) => m.observed_at || '').sort().pop() || null;
    return { revenue, orders, units, refunds, aov, currency, latestObserved };
  }, [measurements, orderRecords, invalidatedOrderRecords]);

  const trend = useMemo(() => {
    const byDate = {};
    orderRecords.forEach((m) => {
      const date = (m.observed_at || '').slice(0, 10);
      if (!date) return;
      // null (a gap), not 0, for a day a metric was not reported.
      byDate[date] = byDate[date] || { date, revenue: null, orders: null };
      if (m.metric_key === 'revenue' && typeof m.value === 'number') byDate[date].revenue = (byDate[date].revenue || 0) + m.value;
      if (m.metric_key === 'orders' && typeof m.value === 'number') byDate[date].orders = (byDate[date].orders || 0) + m.value;
    });
    return Object.values(byDate)
      .map((d) => ({ ...d, aov: d.orders && d.revenue !== null ? d.revenue / d.orders : null }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [orderRecords]);

  const products = useMemo(() => {
    const byProduct = {};
    productRecords.forEach((m) => {
      const key = m.entity_id || 'unknown';
      byProduct[key] = byProduct[key] || { entity_id: key, price: null, inventory: null };
      if (m.metric_key === 'price') byProduct[key].price = m.value;
      if (m.metric_key === 'inventory') byProduct[key].inventory = m.value;
    });
    return Object.values(byProduct).map((row) => ({ ...row, name: productNames[row.entity_id] || row.entity_id }));
  }, [productRecords, productNames]);

  const sortedRaw = useMemo(() => {
    if (!measurements) return [];
    const copy = [...measurements];
    copy.sort((a, b) => {
      const av = a[sortKey], bv = b[sortKey];
      if (av === bv) return 0;
      const cmp = (av ?? '') < (bv ?? '') ? -1 : 1;
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [measurements, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('desc'); }
  };

  const loading = connectors === null || measurements === null;

  return (
    <DashboardLayout>
      <div data-testid="ecommerce-page" className="max-w-5xl mx-auto">
        <div className="mb-4">
          <h1 className="font-display text-[20px] font-bold text-[var(--text-primary)] mb-1">Ecommerce</h1>
          <p className="text-[12px] text-[var(--text-secondary)]">Real, connector-synced Shopify performance — never a projected or example number.</p>
        </div>

        {!noClient && clients.length > 0 && (
          <div className="mb-4">
            <WorkspaceSelect clients={clients} workspaceId={workspaceId} setWorkspaceId={setWorkspaceId} />
          </div>
        )}

        {noClient && <EmptyState title="ADD A BRAND FIRST" description="Connect your ecommerce platform once your workspace exists." />}

        {!noClient && loading && !error && <LoadingState />}
        {!noClient && error && <ErrorState error={error} onRetry={load} />}

        {!noClient && !error && !loading && !isConnected && (
          <EmptyState
            testId="ecommerce-not-connected"
            title="Shopify is not connected"
            description="Ecommerce metrics come from a Shopify store connection, which is not connected on this workspace. Google Analytics traffic is a separate Analytics connection and is not used as sales here. Martian will never show an estimated revenue, order, or AOV number in place of real data."
            actionLabel="Go to Data Connections"
            onAction={() => navigate('/growth/data-connections')}
          />
        )}

        {!noClient && !error && !loading && isConnected && (
          <div className="space-y-6">
            <div className="flex items-center gap-2 flex-wrap" data-testid="ecommerce-connected-status">
              <span className="flex items-center gap-1.5 text-[11px] text-[var(--text-secondary)]">
                Shopify <StatusBadge value={connectedShopify.status} /> <StatusBadge value={connectedShopify.last_sync_status} />
              </span>
              {connectedShopify.last_sync_at && (
                <span className="text-[11px] text-[var(--text-tertiary)]" data-testid="ecommerce-last-synced">Last synced {new Date(connectedShopify.last_sync_at).toLocaleString()}</span>
              )}
              {summary?.latestObserved && (
                <span className="text-[11px] text-[var(--text-tertiary)]" data-testid="ecommerce-data-through">· Data through {summary.latestObserved.slice(0, 10)}</span>
              )}
              {connectionNote(connectedShopify) && (
                <span className="text-[11px] text-[var(--warning)]" data-testid="ecommerce-sync-note">{connectionNote(connectedShopify)}</span>
              )}
              <button
                type="button"
                data-testid="ecommerce-report-refresh"
                onClick={refreshReport}
                disabled={refreshingReport}
                className="text-[12px] underline text-[var(--text-secondary)] disabled:opacity-60"
              >
                {refreshingReport ? 'Refreshing the 60-day window…' : 'Refresh the latest 60 days'}
              </button>
            </div>
            {refreshError && (
              <p className="text-[12.5px] text-[var(--warning)]" data-testid="ecommerce-report-refresh-error">{refreshError}</p>
            )}

            {commerceReport?.state === 'ready' && commerceReport.overview && (
              <div className="rounded-[var(--radius-card,12px)] border border-[var(--border)] p-4 space-y-2" data-testid="ecommerce-60day-report">
                <p className="text-[12px] font-semibold uppercase tracking-[0.06em] text-[var(--text-tertiary)]">Latest 60-day Shopify window</p>
                <p className="text-[12.5px] text-[var(--text-secondary)]">{commerceReport.history_limit_note}</p>
                <p className="text-[12.5px] text-[var(--text-secondary)]" data-testid="ecommerce-report-attribution">These figures are Shopify store orders. They do not show which marketing channel caused a sale.</p>
                <p className="text-[12.5px] text-[var(--text-tertiary)]">
                  {commerceReport.currency || 'Currency not reported'}
                  {commerceReport.timezone ? ` · ${commerceReport.timezone}` : ''}
                  {commerceReport.data_through ? ` · through ${commerceReport.data_through}` : ''}
                </p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-[13px]">
                  {['orders', 'gross_sales', 'discounts', 'refunds', 'net_sales', 'average_order_value'].map((key) => {
                    const metric = commerceReport.overview[key] || {};
                    const label = key.replace(/_/g, ' ');
                    return (
                      <div key={key} data-testid={`ecommerce-window-${key}`} data-state={metric.state}>
                        <p className="text-[11px] text-[var(--text-tertiary)] capitalize">{label}</p>
                        <p className="tabular-nums">
                          {metric.state === 'measured' ? formatNumber(metric.value, key === 'orders' ? 0 : 2) : (METRIC_STATE_NOTE[metric.state] || 'Not measured')}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {commerceReport?.state === 'not_synced' && (
              <p className="text-[12.5px] text-[var(--text-secondary)]" data-testid="ecommerce-report-not-synced">{commerceReport.message}</p>
            )}
            {commerceReport?.state === 'reconnect_required' && (
              <p className="text-[12.5px] text-[var(--warning)]" data-testid="ecommerce-report-reconnect">{commerceReport.message}</p>
            )}

            {measurements.length === 0 ? (
              <EmptyState
                testId="ecommerce-connected-no-data"
                title="Connected, no data yet"
                description="Your Shopify store is connected but Martian has not synced any measurements yet."
                actionLabel="Go to Data Connections"
                onAction={() => navigate('/growth/data-connections')}
              />
            ) : (
              <>
                <div>
                  <p className="text-[11px] font-mono uppercase tracking-wide text-[var(--text-tertiary)] mb-2">Summary</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="ecommerce-summary">
                    {[
                      ['Revenue', summary.revenue, (v) => formatMoney(v, summary.currency.code)],
                      ['Orders', summary.orders, (v) => formatNumber(v)],
                      ['AOV', summary.aov, (v) => formatMoney(v, summary.currency.code)],
                      ['Units sold', summary.units, (v) => formatNumber(v)],
                      ['Refunds', summary.refunds, (v) => formatMoney(v, summary.currency.code)],
                    ].map(([label, metric, fmt]) => (
                      <div key={label} data-testid={`ecommerce-metric-${label.toLowerCase().replace(/\s+/g, '-')}`} data-state={metric.state} className="rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface)] p-4 shadow-[var(--shadow-card)]">
                        <p className="text-[10px] font-mono uppercase tracking-wide text-[var(--text-tertiary)] mb-1.5">{label}</p>
                        {metric.value === null || metric.value === undefined ? (
                          <>
                            <p className="text-[16px] font-semibold text-[var(--text-tertiary)]">—</p>
                            <p className="mt-0.5 text-[11px] text-[var(--text-tertiary)]">{METRIC_STATE_NOTE[metric.state] || 'Not available'}</p>
                          </>
                        ) : (
                          <p className="text-[16px] font-semibold text-[var(--text-primary)]">{fmt(metric.value)}</p>
                        )}
                      </div>
                    ))}
                  </div>
                  <p className="mt-2 text-[11px] text-[var(--text-tertiary)]" data-testid="ecommerce-currency-note">
                    {summary.currency.mixed
                      ? 'These orders are in more than one currency, so money totals are not added up.'
                      : summary.currency.code
                        ? `Amounts in ${summary.currency.code}, as reported by Shopify.`
                        : 'Currency not provided by the connector, so amounts are shown without one.'}
                    {' '}“Not reported” means Shopify has not sent that metric; it is not counted as zero.
                  </p>
                </div>

                {trend.length > 1 && (
                  <div>
                    <p className="text-[11px] font-mono uppercase tracking-wide text-[var(--text-tertiary)] mb-2">Trends</p>
                    <div className="rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface)] p-4 shadow-[var(--shadow-card)]" style={{ height: 260 }} data-testid="ecommerce-trend-chart">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={trend}>
                          <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                          <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                          <YAxis tick={{ fontSize: 10 }} />
                          <Tooltip />
                          <Line type="monotone" dataKey="revenue" stroke="var(--accent)" strokeWidth={2} dot={false} name="Revenue" />
                          <Line type="monotone" dataKey="orders" stroke="var(--success)" strokeWidth={2} dot={false} name="Orders" />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}

                {products.length > 0 && (
                  <div>
                    <p className="text-[11px] font-mono uppercase tracking-wide text-[var(--text-tertiary)] mb-2">Products</p>
                    <p className="text-[10px] text-[var(--text-tertiary)] mb-2">Shopify reports price and inventory per product; order revenue is recorded at the store level, not per product, so it is not shown per row here.</p>
                    <div className="rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface)] overflow-x-auto" data-testid="ecommerce-products-table">
                      <table className="w-full text-[11px]">
                        <thead>
                          <tr className="border-b border-[var(--border)] text-left text-[var(--text-tertiary)]">
                            <th className="px-3 py-2">Product</th>
                            <th className="px-3 py-2">Price</th>
                            <th className="px-3 py-2">Inventory</th>
                          </tr>
                        </thead>
                        <tbody>
                          {products.map((p) => (
                            <tr key={p.entity_id} className="border-b border-[var(--border-light,var(--border))] last:border-b-0">
                              <td className="px-3 py-2 text-[var(--text-primary)]">{p.name}</td>
                              <td className="px-3 py-2 text-[var(--text-secondary)]">{p.price === null ? '—' : formatNumber(p.price, 2)}</td>
                              <td className="px-3 py-2 text-[var(--text-secondary)]">{p.inventory === null ? '—' : formatNumber(p.inventory)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                <div>
                  <p className="text-[11px] font-mono uppercase tracking-wide text-[var(--text-tertiary)] mb-2">Raw data</p>
                  <p className="text-[10px] text-[var(--text-tertiary)] mb-2">Click a row to see the full stored measurement record.</p>
                  <div className="rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface)] overflow-x-auto" data-testid="ecommerce-raw-table">
                    <table className="w-full text-[11px]">
                      <thead>
                        <tr className="border-b border-[var(--border)] text-left text-[var(--text-tertiary)]">
                          {['observed_at', 'entity_type', 'entity_id', 'metric_key', 'value', 'unit'].map((col) => (
                            <th key={col} className="px-3 py-2 cursor-pointer select-none" onClick={() => toggleSort(col)} data-testid={`ecommerce-sort-${col}`}>
                              {col}{sortKey === col ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                            </th>
                          ))}
                          <th className="px-3 py-2">status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedRaw.slice(0, 200).map((m) => (
                          <tr
                            key={m.id} data-testid={`ecommerce-raw-row-${m.id}`}
                            className="border-b border-[var(--border-light,var(--border))] last:border-b-0 cursor-pointer hover:bg-[var(--surface-sunken,var(--canvas-secondary))] focus-within:bg-[var(--surface-sunken,var(--canvas-secondary))]"
                            onClick={() => setSelectedMeasurement(m)}
                            tabIndex={0}
                            onKeyDown={(e) => { if (e.key === 'Enter') setSelectedMeasurement(m); }}
                          >
                            <td className="px-3 py-2 text-[var(--text-secondary)] font-mono">{m.observed_at?.slice(0, 10)}</td>
                            <td className="px-3 py-2 text-[var(--text-tertiary)]">{m.entity_type}</td>
                            <td className="px-3 py-2 text-[var(--text-secondary)] font-mono">{productNames[m.entity_id] || m.entity_id}</td>
                            <td className="px-3 py-2 text-[var(--text-primary)] font-mono">{m.metric_key}</td>
                            <td className="px-3 py-2 text-[var(--text-primary)]">{formatNumber(m.value, 2)}</td>
                            <td className="px-3 py-2 text-[var(--text-tertiary)]">{m.unit}</td>
                            <td className="px-3 py-2">{m.status ? <StatusBadge value={m.status} /> : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {sortedRaw.length > 200 && (
                    <p className="text-[10px] text-[var(--text-tertiary)] mt-1.5">Showing the first 200 of {sortedRaw.length} records.</p>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* ── Raw measurement detail ── */}
        <Sheet open={!!selectedMeasurement} onOpenChange={(o) => !o && setSelectedMeasurement(null)}>
          <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto bg-[var(--surface)] border-[var(--border)]" data-testid="measurement-detail">
            {selectedMeasurement && (
              <>
                <SheetHeader>
                  <SheetTitle className="text-[var(--text-primary)] text-[14px]">{selectedMeasurement.metric_key}</SheetTitle>
                  <SheetDescription className="text-[var(--text-tertiary)] text-[11px]">The real, stored measurement record.</SheetDescription>
                </SheetHeader>
                <div className="mt-3 space-y-3 text-[12px]">
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Entity</p><p className="text-[var(--text-primary)]">{productNames[selectedMeasurement.entity_id] || selectedMeasurement.entity_id}</p></div>
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Entity type</p><p className="text-[var(--text-primary)] font-mono">{selectedMeasurement.entity_type ?? '—'}</p></div>
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Value</p><p className="text-[var(--text-primary)]">{formatNumber(selectedMeasurement.value, 2)} {selectedMeasurement.unit || ''}</p></div>
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Observed at</p><p className="text-[var(--text-primary)]">{selectedMeasurement.observed_at ?? '—'}</p></div>
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Source type</p><p className="text-[var(--text-primary)] font-mono">{selectedMeasurement.source_type ?? '—'}</p></div>
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Status</p><p>{selectedMeasurement.status ? <StatusBadge value={selectedMeasurement.status} /> : '—'}</p></div>
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Confidence</p><ConfidenceIndicator value={selectedMeasurement.confidence} /></div>
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Baseline value</p><p className="text-[var(--text-primary)]">{selectedMeasurement.baseline_value === null || selectedMeasurement.baseline_value === undefined ? '—' : formatNumber(selectedMeasurement.baseline_value, 2)}</p></div>
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Comparison value</p><p className="text-[var(--text-primary)]">{selectedMeasurement.comparison_value === null || selectedMeasurement.comparison_value === undefined ? '—' : formatNumber(selectedMeasurement.comparison_value, 2)}</p></div>
                  </div>
                  <Separator className="bg-[var(--border)]" />
                  <div>
                    <p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Source reference</p>
                    <p className="text-[var(--text-tertiary)] font-mono break-all">{selectedMeasurement.source_reference ?? '—'}</p>
                  </div>
                  {selectedMeasurement.metadata && Object.keys(selectedMeasurement.metadata).length > 0 && (
                    <>
                      <Separator className="bg-[var(--border)]" />
                      <div>
                        <p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)] mb-1">Metadata</p>
                        <div className="space-y-1" data-testid="detail-measurement-metadata">
                          {Object.entries(selectedMeasurement.metadata).map(([k, v]) => (
                            <div key={k} className="flex items-center justify-between text-[11px] bg-[var(--surface-sunken,var(--canvas-secondary))] rounded px-2 py-1">
                              <span className="text-[var(--text-tertiary)] font-mono">{k}</span>
                              <span className="text-[var(--text-primary)] font-mono break-all">{String(v)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                  <Separator className="bg-[var(--border)]" />
                  <div>
                    <p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Record ID</p>
                    <p className="text-[var(--text-tertiary)] font-mono break-all">{selectedMeasurement.id ?? '—'}</p>
                  </div>
                </div>
              </>
            )}
          </SheetContent>
        </Sheet>
      </div>
    </DashboardLayout>
  );
}
